import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import PropTypes from 'prop-types';

import { baseFormat, toSimpleUtcMonthString } from 'src/utils/format/date';
import { getCompanyByType } from 'src/utils/form/job';
import { jobPhotoRecordsOrderCmpFn } from 'src/utils/helpers';
import { PHOTO_FORM_PREVIEW_CONST } from 'src/components/JobMobileDataTab/constants';
import Box from 'src/components/Box';
import Svg from 'src/components/Svg';
import s from 'src/components/JobMobileDataTab/PhotoFormPrev.styles';

const DESCRIPTION_LINE_LIMIT = 2;
const FONT_MIN_SIZE = 6;
const DESCRIPTION_SIZE_DECREASE_STEP = 5;

const PhotoFormPrev = props => {

  const { t } = useTranslation();

  const {
    job,
    jobDatasheetResults,
    formPhotoRecords,
  } = props;

  const imagesFirstPage = 6;
  const imagesPerPage = 12;

  const elevatePage = PHOTO_FORM_PREVIEW_CONST.ELEVATE_PAGE;
  const elevateEmail = PHOTO_FORM_PREVIEW_CONST.ELEVATE_EMAIL;

  const [firstPageImages, setFirstPageImages] = useState([]);
  const [imagesList, setImagesList] = useState([]);
  const [numberOfPages, setNumberOfPages] = useState(1);

  const companies = job.jobCompanies.filter(c => !!c.company);
  const factory = getCompanyByType(companies, 'Factory');

  const jobDatasheetResultsFlat = jobDatasheetResults.map(v => v.data).flat(1);

  // Client name
  const clientNameQuestion = jobDatasheetResultsFlat.find(v => v.datasheetQuestionId === PHOTO_FORM_PREVIEW_CONST.CLIENT_NAME_QUES_ID);
  const clientName = clientNameQuestion?.result?.value || '-';

  // Report No.
  const reportNoQuestion = jobDatasheetResultsFlat.find(v => v.datasheetQuestionId === PHOTO_FORM_PREVIEW_CONST.REPORT_NUMBER_QUES_ID);
  const reportNo = reportNoQuestion?.result?.value || job.reportNumber || '-';

  // Facility name
  const facilityName = factory ? factory.company.currentVersion.name : '-';

  // Assessment date
  const getDate = (dateString) => {
    return dateString?.result?.value ? moment.utc(dateString?.result?.value).format(baseFormat) : null;
  };
  const assessmentDateStartQuestion = jobDatasheetResultsFlat.find(v => v.datasheetQuestionId === PHOTO_FORM_PREVIEW_CONST.ASSESSMENT_START_DATE_QUES_ID);
  const assessmentDateStart = getDate(assessmentDateStartQuestion);
  const assessmentDateEndQuestion = jobDatasheetResultsFlat.find(v => v.datasheetQuestionId === PHOTO_FORM_PREVIEW_CONST.ASSESSMENT_END_DATE_QUES_ID);
  const assessmentDateEnd = getDate(assessmentDateEndQuestion);

  useEffect(() => {
    const list = [...formPhotoRecords];
    list.sort(jobPhotoRecordsOrderCmpFn);
    const images = list.slice(imagesFirstPage);
    setFirstPageImages(list.slice(0, imagesFirstPage));
    setImagesList(images);
    setNumberOfPages(Math.ceil(images.length / imagesPerPage));

  }, [job.id]);

  const ref = useRef();
  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const getLineHeight = (el) => {
      const lineHeight = el.style.lineHeight ? el.style.lineHeight : getComputedStyle(el)?.lineHeight;
      return parseInt(lineHeight, 10);
    };

    const getLinesCount = (el) => {
      const height = el.offsetHeight;
      const lineHeight = getLineHeight(el);
      return height / lineHeight;
    };

    const descriptionCells = ref.current.querySelectorAll('span > div');

    /* eslint-disable no-param-reassign */
    descriptionCells.forEach(descriptionCell => {
      if (descriptionCell.hasAttribute('fitted')) {
        return;
      }

      const { textContent } = descriptionCell;
      let textLength = textContent.length;
      let step = 0;

      while (getLinesCount(descriptionCell) > DESCRIPTION_LINE_LIMIT) {
        const currentSize = getLineHeight(descriptionCell);
        const nextSize = currentSize - 1;
        if (nextSize >= FONT_MIN_SIZE) {
          descriptionCell.style.lineHeight = `${nextSize}px`;
          descriptionCell.style.fontSize = `${nextSize}px`;
        } else {
          textLength -= DESCRIPTION_SIZE_DECREASE_STEP;
          descriptionCell.textContent = `${textContent.substr(0, textLength)}…`;
        }
        step += 1;
      }

      descriptionCell.setAttribute('fitted', true);
    });
    /* eslint-enable no-param-reassign */
  });

  const renderImage = (image, index) => {
    const tags = image.tags.map(tag => tag.name).filter(tag => tag).join(', ');

    return (
      <li className="image-preview">
        <div className="image-outer">
          <img alt={t('PHOTO_FORM_IMAGE_ALT_TEXT')} src={image.src} />
        </div>
        <s.Caption finding={!!image.showFindings}>
          <div>{index}{image.showFindings ? ` (${t('FINDING')})` : ''}: {tags}</div>
        </s.Caption>
      </li>
    );
  };

  const renderPageNumber = (number) => (
    <s.PageNumber>
      {t('PAGE_OF', { from: number, to: numberOfPages + 1 })}
    </s.PageNumber>
  );

  const renderHeader = () => (
    <s.PageA4Header>
      <div className="logo-wrapper">
          <img src="/lrqa_logo.png" />
      </div>
    </s.PageA4Header>
  );

  const renderFooter = (pageNumber) => (
    <s.PageA4Footer>
      <s.FooterDateText>{t('PHOTO_FORM_DATE', { date: toSimpleUtcMonthString(Date.now()) })}</s.FooterDateText>
      {renderPageNumber(pageNumber)}
    </s.PageA4Footer>
  );

  const renderPage = (page) => {
    const pageNumber = page + 2; // We start from page 2, and page is from 0
    const imagesOffset = page * imagesPerPage;
    const list = imagesList.slice(imagesOffset, imagesOffset + imagesPerPage);
    const imageNumber = (index) => ((index + 1) + ((page) * imagesPerPage) + imagesFirstPage);

    return (
      <s.PageA4 key={`page_key_${page}`}>
        <s.PageA4Inner>
          {renderHeader()}
          <s.ImageWrapper>
            {list.map((image, index) => renderImage(image, imageNumber(index)))}
          </s.ImageWrapper>

          {renderFooter(pageNumber)}
        </s.PageA4Inner>
      </s.PageA4>
    );
  };

  const renderPages = () => {
    const pages = [];

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < numberOfPages; i++) {
      pages.push(renderPage(i));
    }

    return pages;
  };

  const renderFirstPage = () => (
    <s.PageA4>
      <s.PageA4Inner>
        {renderHeader()}
        <s.Wrapper>
          <s.Title>{t('PHOTO_FORM')}</s.Title>
          <s.SectionTitle>{t('ASSESSMENT_DETAILS')}</s.SectionTitle>
          <s.Fields>
            <s.Field blue label={t('CLIENT_NAME')} text={clientName} />
            <s.Field gray label={t('JOB_REPORT_NUMBER')} text={reportNo} />

            <s.Field blue label={t('FACILITY_NAME')} text={facilityName} />
            <s.Field gray label={t('ASSESSMENT_DATE')} text={`${assessmentDateStart} - ${assessmentDateEnd}`} />
          </s.Fields>
        </s.Wrapper>

        <s.Wrapper>
          <s.SectionTitle>{t('B_PHOTOS')}</s.SectionTitle>
          <s.PhotoSectionUpperText className="justify">{t('PHOTO_FORM_TEXT')}</s.PhotoSectionUpperText>
          <s.ImageWrapper>
            {firstPageImages.map((image, index) => renderImage(image, index + 1))}
          </s.ImageWrapper>
        </s.Wrapper>

        {renderFooter(1)}
      </s.PageA4Inner>
    </s.PageA4>
  );

  return (
    <Box withoutBorder>
      <s.Title>{t('PHOTO_FORM')}</s.Title>
      <s.PageWrapper ref={ref}>
        {renderFirstPage()}
        {renderPages()}
      </s.PageWrapper>
    </ Box>
  );
};

PhotoFormPrev.propTypes = {
  job: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    reportNumber: PropTypes.string,
    jobCompanies: PropTypes.arrayOf(
      PropTypes.shape({
        companyTypeId: PropTypes.number,
        company: PropTypes.shape({
          id: PropTypes.number,
          currentVersion: PropTypes.shape({
            name: PropTypes.string,
            companyTypes: PropTypes.arrayOf(PropTypes.shape({})),
            companyAddresses: PropTypes.arrayOf(
              PropTypes.shape({
                currentVersion: PropTypes.shape({
                  address1: PropTypes.string,
                  countryId: PropTypes.number,
                }),
              }),
            ),
          }),
        }),
      }),
    ),
  }).isRequired,
  formPhotoRecords: PropTypes.array,
  jobDatasheetResults: PropTypes.array,
};

PhotoFormPrev.defaultProps = {
  formPhotoRecords: [],
  jobDatasheetResults: [],
};

export default React.memo(PhotoFormPrev);

